// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — Data Flow Diagram
//
// LeanIX ships a "Data Flow" diagram type; reviewing a live tenant made it
// obvious why. Once the Epic 3rd-Party Testing Runbook was loaded as Interface
// fact sheets we finally have the data to draw the real one: which third-party
// systems exchange data with the core system, whether that traffic goes THROUGH
// the interface engine or point-to-point around it, by which network path, and
// via which Epic interface mechanism.
//
// The single most useful mark on this page is the split between the two sides:
//
//   left of the engine   → traffic that routes through the interface engine.
//                          One chokepoint, one cutover conversation.
//   right of the core    → DIRECT point-to-point integrations. No engine, no
//                          central place to re-point, no throttle. These are
//                          the migration risk, so they are drawn on their own
//                          side, in heavier line weight, and counted.
//
// Four things this screen refuses to do:
//   1. Invent an edge. Every line corresponds to one Interface fact sheet. If
//      the routing is not recorded, the edge is dashed and says so; it is not
//      quietly promoted to "direct" or "via engine".
//   2. Invent a mechanism. When epicE0E / epicE0A / epicFDI / epicAIP are all
//      empty and there is no FHIR override, the edge reads "mechanism not
//      recorded" — never a plausible-looking guess.
//   3. Hide the unlinked. 80 of the 103 interfaces in the NGHS workspace have
//      no application fact sheet behind them. An interface nobody owns is a
//      discovery finding, so it is drawn, marked with a hollow dot, counted,
//      and filterable. Where a link DOES exist it is carried with its
//      linkConfidence and linkMethod and labelled "inferred", never "confirmed".
//   4. Flatten "Other". 35 of 103 carry networkFlow = "Other" — that is an
//      unclassified bucket, not a fourth peer category, and it is drawn grey
//      with a dashed outline so it never reads as a decision that was made.
//
// Rendering: one SVG routine drives the screen, the PDF and the PNG, so what a
// consultant sees is exactly what the client receives. (RelationMap keeps a
// separate ReactFlow canvas because its nodes are plain; these carry badges,
// markers and hover detail, and a second render path would be a second thing to
// keep honest.) Exports reuse exports.js — PDF through printElementToPdf, which
// copies innerHTML, so the map must be SVG; a <canvas> would arrive blank, the
// exact trap PortfolioReport hit.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Divider, MenuItem, Paper,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DownloadIcon from '@mui/icons-material/Download';
import HubIcon from '@mui/icons-material/Hub';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { typeMeta } from './schema';
import { exportCanvasPng, exportCsv, printElementToPdf } from './exports';
import PrimordiaLogo from './PrimordiaLogo';
import store from './store';

// ---- Optimum palette -------------------------------------------------------
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';

const FONT = "-apple-system, 'Segoe UI', Roboto, Arial, sans-serif";
const PRINT_ID = 'dataflow-print';

// ---------------------------------------------------------------------------
// Geometry.
//
// The obvious drawing for "hub and spoke" is a radial fan, and it is wrong at
// this size. A radial layout can only hold about `outerRadius × span / rowStep`
// boxes before spokes start passing behind other boxes; at 77 systems that
// needs a radius near 1,500 px (a 3,000 px tall page), and anything tighter
// produces boxes that overlap and spokes that appear to terminate on the wrong
// node. Measured on the real NGHS counts: 34 overlapping boxes and 238 occluded
// spokes. A diagram that ambiguous is worse than none.
//
// So the two sides use the two shapes that are provably clean, and the shapes
// happen to say the right thing:
//
//   engine side → a COMB. Systems sit in columns, each with its own short stub
//                 into a collector bus, and the bus enters the engine along a
//                 clear corridor. That is what an interface engine physically
//                 is, and no line ever crosses a box.
//   direct side → a COLUMN of individual point-to-point lines straight into the
//                 core system. Every line is its own; nothing is bundled,
//                 because nothing about a direct integration is shared.
// ---------------------------------------------------------------------------
const NODE_W = 168;
const NODE_H = 40;
const COL_STEP = NODE_H + 12;   // vertical pitch inside a column
const HUB_W = 224;
const HUB_H = 64;
const ENG_W = 212;
const ENG_H = 58;

const STUB = 108;               // per-interface line from a system into the bus
const CHAN_GAP = 42;            // bus channel → next column of systems
const COMB_PITCH = STUB + NODE_W + CHAN_GAP;
const CORRIDOR = 64;            // clear band the engine bus runs along
const ENG_BUS_GAP = 46;         // engine node → nearest bus channel
const COMB_TARGET_ROWS = 26;    // rows per column before opening another column
const MAX_COMB_COLS = 6;
const BAND_GAP = 110;           // vertical gap between stacked engine bands
const TRUNK_GAP = 210;          // engine → core
const CORE_COL_GAP = 190;       // core → the direct column

const EDGE_LABEL_LIMIT = 40;    // above this, per-edge mechanism labels stop being readable

// Printable area: Letter/A4 landscape at 10 mm margins, the smaller of the two,
// so one PDF prints uncropped on either.
const PAGE_W = 970;
const PAGE_H = 715;
const MAP_H = PAGE_H - 26;

// ---- Line weights / colours by routing --------------------------------------
const TRUNK_COLOR = NAVY;
const DIRECT_ACCENT = '#C62828';
const DIRECT_WIDTH = 2.4;
const ENGINE_WIDTH = 1.6;

// ---------------------------------------------------------------------------
// Network path.
//
// Four values come out of the runbook. Three of them are real decisions that
// drive firewall, egress and latency work in a migration. "Other" is not — it
// is the bucket the runbook used when nobody classified the flow, and it is the
// second-largest group in the NGHS data, so it is drawn as unclassified rather
// than as a peer category. Anything the source says that is not one of the four
// keeps its own row (with its verbatim label) instead of being folded away.
// ---------------------------------------------------------------------------
const NET_KNOWN = [
  {
    key: 'external via internet',
    label: 'External via Internet',
    color: '#C62828',
    fill: '#fdeeee',
    note: 'public egress, DNS and firewall rules to rebuild',
  },
  {
    key: 'external via vpn',
    label: 'External via VPN',
    color: '#EF6C00',
    fill: '#fff4e5',
    note: 'site-to-site tunnel to re-terminate in the cloud',
  },
  {
    key: 'on-prem only communication',
    label: 'On-Prem Only Communication',
    color: '#2E7D32',
    fill: '#eaf5ec',
    note: 'never leaves the datacentre today — becomes a new path after migration',
  },
  {
    key: 'other',
    label: 'Other',
    color: '#78909C',
    fill: '#eceff1',
    unclassified: true,
    note: 'unclassified in the source runbook — the network path is still unknown',
  },
];
const NET_UNRECORDED = {
  key: '__unrecorded__',
  label: 'Network path not recorded',
  color: '#B0BEC5',
  fill: '#f4f6f8',
  unclassified: true,
  note: 'the source row left this blank',
};

const NET_BY_KEY = new Map(NET_KNOWN.map((n) => [n.key, n]));

// ---- Epic interface mechanisms ---------------------------------------------
const MECHANISM_FIELDS = [
  { field: 'epicE0E', label: 'E0E' },
  { field: 'epicE0A', label: 'E0A' },
  { field: 'epicFDI', label: 'FDI' },
  { field: 'epicAIP', label: 'AIP' },
];
// A FHIR override cell holds free text; these are the values that mean "no".
const FHIR_NEGATIVE = new Set(['', 'no', 'n', 'false', '0', 'none', 'n/a', 'na', '-', '--']);

const MECH_NOT_RECORDED = 'mechanism not recorded';

const ALL = '__all__';

// ---- Small helpers ----------------------------------------------------------
const clean = (v) => String(v === undefined || v === null ? '' : v).replace(/\s+/g, ' ').trim();
const norm = (v) => clean(v).toLowerCase();
const nowStamp = () => new Date().toLocaleString();
const fileStamp = () => new Date().toISOString().slice(0, 10);

// Fields land at `fs.data` when they arrive from the interface import (Postgres
// jsonb) and at `fs.fields` when they were typed in the UI. Read both; a UI edit
// is the later statement, so it wins.
const dataOf = (fs) => ({ ...((fs && fs.data) || {}), ...((fs && fs.fields) || {}) });

function asIdList(v) {
  if (Array.isArray(v)) return v.map(clean).filter(Boolean);
  const s = clean(v);
  if (!s) return [];
  return s.split(/[\s,;]+/).map(clean).filter(Boolean);
}

function svgEsc(s) {
  return String(s === undefined || s === null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function wrapLabel(name, perLine, maxLines) {
  const words = String(name || '').split(/\s+/).filter(Boolean);
  const packed = [];
  let cur = '';
  words.forEach((w) => {
    if (!cur) { cur = w; return; }
    if ((`${cur} ${w}`).length <= perLine) cur = `${cur} ${w}`;
    else { packed.push(cur); cur = w; }
  });
  if (cur) packed.push(cur);

  const lines = [];
  packed.forEach((l) => {
    let s = l;
    while (s.length > perLine) { lines.push(s.slice(0, perLine)); s = s.slice(perLine); }
    if (s) lines.push(s);
  });
  if (!lines.length) return [''];
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = `${kept[maxLines - 1].slice(0, Math.max(1, perLine - 1))}…`;
    return kept;
  }
  return lines;
}

// Where a straight line from a node centre leaves that node's box.
function boxExit(node, tx, ty) {
  const dx = tx - node.cx;
  const dy = ty - node.cy;
  if (dx === 0 && dy === 0) return { x: node.cx, y: node.cy };
  const sx = dx === 0 ? Infinity : (node.w / 2) / Math.abs(dx);
  const sy = dy === 0 ? Infinity : (node.h / 2) / Math.abs(dy);
  const s = Math.min(sx, sy);
  return { x: node.cx + dx * s, y: node.cy + dy * s };
}

// ---------------------------------------------------------------------------
// Comb layout for the engine side. Coordinates are local to the engine node,
// which sits at (0, 0). Reading order is preserved: column by column, top to
// bottom, so the same filter always yields the same picture — it matters when
// the same diagram is re-issued to a client.
//
// Nothing here can cross anything: the stub lane, the bus channel and the
// corridor are each kept clear of every box by construction.
// ---------------------------------------------------------------------------
function combLayout(items) {
  const n = items.length;
  if (!n) return { placed: [], columns: [], minY: -ENG_H / 2, maxY: ENG_H / 2 };
  const cols = Math.max(1, Math.min(MAX_COMB_COLS, Math.ceil(n / COMB_TARGET_ROWS)));
  const rows = Math.ceil(n / cols);
  const above = Math.ceil(rows / 2);
  const engLeft = -ENG_W / 2;

  const placed = [];
  const columns = [];
  for (let c = 0; c < cols; c += 1) {
    const group = items.slice(c * rows, (c + 1) * rows);
    if (!group.length) break;
    const busX = engLeft - ENG_BUS_GAP - c * COMB_PITCH;
    let lo = 0;
    let hi = 0;
    group.forEach((item, j) => {
      const up = j < above;
      const k = up ? (above - 1 - j) : (j - above);
      const cy = (up ? -1 : 1) * (CORRIDOR / 2 + NODE_H / 2 + k * COL_STEP);
      lo = Math.min(lo, cy);
      hi = Math.max(hi, cy);
      placed.push({ ...item, busX, cx: busX - STUB - NODE_W / 2, cy });
    });
    columns.push({ busX, minY: lo, maxY: hi });
  }

  const minY = Math.min(-ENG_H / 2, ...placed.map((p) => p.cy - NODE_H / 2));
  const maxY = Math.max(ENG_H / 2, ...placed.map((p) => p.cy + NODE_H / 2));
  return { placed, columns, minY, maxY };
}

// Column layout for the direct side: one vertical stack, each system on its own
// line to the core. A line leaves its own box's edge and is immediately outside
// the column's x-range, so it can never pass through another box.
function columnLayout(items, cx) {
  const n = items.length;
  if (!n) return [];
  const height = (n - 1) * COL_STEP;
  return items.map((item, j) => ({ ...item, cx, cy: j * COL_STEP - height / 2 }));
}

// Arrowheads are keyed off the stroke colour rather than a hand-kept list, so a
// network path value we have never seen still gets a matching arrow instead of
// silently losing one.
const markerId = (color) => `dfArrow${String(color).replace(/[^0-9a-zA-Z]/g, '')}`;

// ---------------------------------------------------------------------------
// SVG drawing. No foreignObject, no external fonts, no network requests: it has
// to rasterise onto a canvas and survive a print dialog.
// ---------------------------------------------------------------------------
function svgSystemNode(n, selectedId) {
  const net = n.net;
  const selected = n.id === selectedId;
  const lines = wrapLabel(n.name, 24, 2);
  const startY = lines.length === 1 ? n.y + 24 : n.y + 18;
  const parts = [];
  parts.push(`<g data-iface="${svgEsc(n.id)}" style="cursor:pointer">`);
  parts.push(`<title>${svgEsc(n.hover)}</title>`);
  parts.push(
    `<rect x="${n.x}" y="${n.y}" width="${NODE_W}" height="${NODE_H}" rx="6" fill="${net.fill}" stroke="${selected ? NAVY : net.color}" stroke-width="${selected ? 3 : 1.6}"${net.unclassified ? ' stroke-dasharray="5 3"' : ''}/>`
  );
  parts.push(`<rect x="${n.x}" y="${n.y + 4}" width="4" height="${NODE_H - 8}" rx="2" fill="${net.color}"/>`);
  lines.forEach((l, i) => {
    parts.push(
      `<text x="${n.x + 11}" y="${startY + i * 11.5}" font-family="${FONT}" font-size="9.5" font-weight="600" fill="${NAVY}">${svgEsc(l)}</text>`
    );
  });
  if (n.criticality) {
    parts.push(`<rect x="${n.x + NODE_W - 21}" y="${n.y + 4}" width="17" height="12" rx="2.5" fill="${NAVY}"/>`);
    parts.push(
      `<text x="${n.x + NODE_W - 12.5}" y="${n.y + 12.8}" text-anchor="middle" font-family="${FONT}" font-size="8.5" font-weight="700" fill="#ffffff">${svgEsc(n.criticality)}</text>`
    );
  }
  // Filled dot = an application fact sheet is linked (inferred, confidence kept
  // on the record); hollow dot = nothing behind this interface.
  parts.push(
    n.linked
      ? `<circle cx="${n.x + NODE_W - 12}" cy="${n.y + NODE_H - 10}" r="4.2" fill="${BLUE}"/>`
      : `<circle cx="${n.x + NODE_W - 12}" cy="${n.y + NODE_H - 10}" r="4.2" fill="#ffffff" stroke="${GREY}" stroke-width="1.3" stroke-dasharray="2 1.6"/>`
  );
  parts.push('</g>');
  return parts.join('');
}

function svgHubNode(n) {
  const lines = wrapLabel(n.name, 22, 1);
  return [
    `<g><rect x="${n.x}" y="${n.y}" width="${n.w}" height="${n.h}" rx="9" fill="${n.fill}" stroke="${n.stroke}" stroke-width="2.4"/>`,
    `<text x="${n.cx}" y="${n.y + n.h / 2 - 2}" text-anchor="middle" font-family="${FONT}" font-size="13" font-weight="700" fill="#ffffff">${svgEsc(lines[0])}</text>`,
    `<text x="${n.cx}" y="${n.y + n.h / 2 + 13}" text-anchor="middle" font-family="${FONT}" font-size="9.5" fill="#d9e6f4">${svgEsc(n.sub)}</text>`,
    '</g>',
  ].join('');
}

// Handles both node-to-node edges (clipped to each box) and raw-coordinate
// segments (the collector bus), which arrive in graph coordinates and are
// translated here exactly as the nodes were.
function svgEdge(e, nodeById, withLabel, off) {
  let p1;
  let p2;
  if (e.raw) {
    p1 = { x: e.x1 - off.x, y: e.y1 - off.y };
    p2 = { x: e.x2 - off.x, y: e.y2 - off.y };
  } else {
    const a = nodeById.get(e.source);
    const b = nodeById.get(e.target);
    if (!a || !b) return '';
    p1 = boxExit(a, b.cx, b.cy);
    p2 = boxExit(b, a.cx, a.cy);
  }
  const dash = e.dashed ? ' stroke-dasharray="6 4"' : '';
  const marker = e.noMarker ? '' : ` marker-end="url(#${markerId(e.color)})"`;
  const line = `<line x1="${p1.x.toFixed(1)}" y1="${p1.y.toFixed(1)}" x2="${p2.x.toFixed(1)}" y2="${p2.y.toFixed(1)}" stroke="${e.color}" stroke-width="${e.width}"${dash}${marker}/>`;
  if (!withLabel || !e.label) return line;
  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2 + (e.labelAbove ? -8 : 0);
  const w = e.label.length * 4.9 + 8;
  return [
    line,
    `<rect x="${(mx - w / 2).toFixed(1)}" y="${(my - 7).toFixed(1)}" width="${w.toFixed(1)}" height="13" rx="3" fill="#ffffff" fill-opacity="0.9"/>`,
    `<text x="${mx.toFixed(1)}" y="${(my + 2.5).toFixed(1)}" text-anchor="middle" font-family="${FONT}" font-size="8.5" fill="${e.labelColor || GREY}">${svgEsc(e.label)}</text>`,
  ].join('');
}

// Legend drawn into the SVG so a PNG stands alone once it leaves the room. The
// PDF gets the same content as HTML, which prints crisper.
function svgLegend(x, y, width, L) {
  const cols = Math.max(1, Math.floor(width / 250));
  const parts = [`<text x="${x}" y="${y + 12}" font-family="${FONT}" font-size="12" font-weight="700" fill="${NAVY}">Legend</text>`];
  let cursor = y + 26;

  parts.push(`<text x="${x}" y="${cursor + 9}" font-family="${FONT}" font-size="10" font-weight="700" fill="${GREY}">NETWORK PATH (node colour)</text>`);
  cursor += 18;
  L.netRows.forEach((n, i) => {
    const c = i % cols;
    const r = Math.floor(i / cols);
    const bx = x + c * 250;
    const by = cursor + r * 18;
    parts.push(`<rect x="${bx}" y="${by}" width="20" height="13" rx="3" fill="${n.fill}" stroke="${n.color}" stroke-width="1.5"${n.unclassified ? ' stroke-dasharray="4 2.5"' : ''}/>`);
    parts.push(`<text x="${bx + 26}" y="${by + 10.5}" font-family="${FONT}" font-size="10" fill="${NAVY}">${svgEsc(`${n.label} (${n.count})`)}</text>`);
  });
  cursor += Math.ceil(L.netRows.length / cols) * 18 + 10;

  parts.push(`<text x="${x}" y="${cursor + 9}" font-family="${FONT}" font-size="10" font-weight="700" fill="${GREY}">ROUTING (which side of the core, and how the line runs)</text>`);
  cursor += 18;
  const routeRows = [
    { w: DIRECT_WIDTH, dash: '', color: DIRECT_ACCENT, text: `RIGHT of ${L.coreName} — direct point-to-point, its own unshared line, no engine (${L.directCount})` },
    { w: ENGINE_WIDTH, dash: '', color: GREY, text: `LEFT — one stub per interface onto the collector bus, then through ${L.engineLabel} (${L.engineCount})` },
    { w: 1.6, dash: ' stroke-dasharray="6 4"', color: GREY, text: `Dashed — whether this interface routes through an engine is not recorded (${L.unknownCount})` },
  ];
  routeRows.forEach((r, i) => {
    const by = cursor + i * 17;
    parts.push(`<line x1="${x}" y1="${by}" x2="${x + 34}" y2="${by}" stroke="${r.color}" stroke-width="${r.w}"${r.dash} marker-end="url(#${markerId(r.color)})"/>`);
    parts.push(`<text x="${x + 44}" y="${by + 3.5}" font-family="${FONT}" font-size="10" fill="${NAVY}">${svgEsc(r.text)}</text>`);
  });
  cursor += routeRows.length * 17 + 4;
  parts.push(`<text x="${x}" y="${cursor + 8}" font-family="${FONT}" font-size="10" fill="${GREY}">${svgEsc('The navy bus and the engine trunk are shared infrastructure, drawn once and labelled with their count. Every interface still has its own coloured stub onto the bus — no line on this page exists without an Interface fact sheet behind it.')}</text>`);
  cursor += 20;

  parts.push(`<text x="${x}" y="${cursor + 9}" font-family="${FONT}" font-size="10" font-weight="700" fill="${GREY}">NODE MARKERS</text>`);
  cursor += 18;
  parts.push(`<circle cx="${x + 8}" cy="${cursor + 4}" r="4.2" fill="${BLUE}"/>`);
  parts.push(`<text x="${x + 20}" y="${cursor + 7.5}" font-family="${FONT}" font-size="10" fill="${NAVY}">${svgEsc(`linked to an application fact sheet — inferred by name match, confidence carried per interface (${L.linkedCount})`)}</text>`);
  cursor += 16;
  parts.push(`<circle cx="${x + 8}" cy="${cursor + 4}" r="4.2" fill="#ffffff" stroke="${GREY}" stroke-width="1.3" stroke-dasharray="2 1.6"/>`);
  parts.push(`<text x="${x + 20}" y="${cursor + 7.5}" font-family="${FONT}" font-size="10" fill="${NAVY}">${svgEsc(`no application fact sheet behind this interface (${L.unlinkedCount}) — a discovery finding, not noise`)}</text>`);
  cursor += 16;
  parts.push(`<rect x="${x}" y="${cursor - 2}" width="17" height="12" rx="2.5" fill="${NAVY}"/>`);
  parts.push(`<text x="${x + 8.5}" y="${cursor + 6.8}" text-anchor="middle" font-family="${FONT}" font-size="8.5" font-weight="700" fill="#ffffff">0</text>`);
  parts.push(`<text x="${x + 24}" y="${cursor + 7}" font-family="${FONT}" font-size="10" fill="${NAVY}">criticality exactly as recorded in the source runbook (0–3); the source does not define the scale, so it is not re-interpreted here</text>`);
  cursor += 20;

  parts.push(`<text x="${x}" y="${cursor + 8}" font-family="${FONT}" font-size="10" fill="${GREY}">${svgEsc(`Edge labels give the Epic interface mechanism; "${MECH_NOT_RECORDED}" means every E0E / E0A / FDI / AIP field was empty. Record numbers are in the interface table and the CSV.`)}</text>`);
  cursor += 15;
  if (L.labelsHidden) {
    parts.push(`<text x="${x}" y="${cursor + 8}" font-family="${FONT}" font-size="10" fill="#8a5a00">${svgEsc(`Edge labels are hidden above ${EDGE_LABEL_LIMIT} drawn interfaces — the mechanism of every interface is in the table and the CSV.`)}</text>`);
    cursor += 15;
  }
  parts.push(`<text x="${x}" y="${cursor + 9}" font-family="${FONT}" font-size="11" font-weight="700" fill="${NAVY}">${svgEsc(`${L.drawn} of ${L.total} interfaces drawn`)}</text>`);
  cursor += 16;

  return { markup: parts.join(''), height: cursor - y };
}

function buildSvg(graph, opts) {
  const o = opts || {};
  const pad = 26;
  const nodes = graph.nodes;
  const rawEdges = graph.edges.filter((e) => e.raw);
  const xs = [
    ...nodes.map((n) => n.cx - n.w / 2), ...nodes.map((n) => n.cx + n.w / 2),
    ...rawEdges.map((e) => e.x1), ...rawEdges.map((e) => e.x2),
  ];
  const ys = [
    ...nodes.map((n) => n.cy - n.h / 2), ...nodes.map((n) => n.cy + n.h / 2),
    ...rawEdges.map((e) => e.y1), ...rawEdges.map((e) => e.y2),
  ];
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const gW = Math.max(...xs) - minX;
  const gH = Math.max(...ys) - minY;

  const headerH = o.header ? 62 : 0;
  const contentW = Math.max(gW, o.header || o.legend ? 720 : 0);

  const placed = nodes.map((n) => ({
    ...n,
    cx: n.cx - minX,
    cy: n.cy - minY,
    x: n.cx - minX - n.w / 2,
    y: n.cy - minY - n.h / 2,
  }));
  const byId = new Map(placed.map((n) => [n.id, n]));

  const body = ['<rect x="0" y="0" width="__W__" height="__H__" fill="#ffffff"/>'];

  if (o.header) {
    const meta = o.meta || {};
    body.push(`<text x="${pad}" y="${pad + 6}" font-family="${FONT}" font-size="16" font-weight="700" fill="${NAVY}">${svgEsc(meta.title || 'Primordia — Data Flow')}</text>`);
    body.push(`<text x="${pad}" y="${pad + 25}" font-family="${FONT}" font-size="11.5" fill="${BLUE}">${svgEsc(meta.scope || '')}</text>`);
    body.push(`<text x="${pad}" y="${pad + 42}" font-family="${FONT}" font-size="10.5" fill="${GREY}">${svgEsc(meta.sub || '')}</text>`);
  }

  const off = { x: minX, y: minY };
  body.push(`<g transform="translate(${pad},${pad + headerH})">`);
  graph.edges.forEach((e) => body.push(svgEdge(e, byId, o.labels, off)));
  placed.forEach((n) => {
    if (n.kind === 'system') body.push(svgSystemNode(n, o.selectedId));
    else body.push(svgHubNode(n));
  });
  body.push('</g>');

  let legendH = 0;
  if (o.legend && o.legendModel) {
    const lg = svgLegend(pad, pad + headerH + gH + 26, Math.max(contentW, 660), o.legendModel);
    legendH = lg.height + 26;
    body.push(lg.markup);
  }

  const width = Math.round(Math.max(contentW, 660) + pad * 2);
  const height = Math.round(gH + headerH + legendH + pad * 2);

  const arrow = (color) =>
    `<marker id="${markerId(color)}" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="${color}"/></marker>`;
  const arrowColors = [...new Set([
    TRUNK_COLOR, DIRECT_ACCENT, GREY,
    ...graph.edges.map((e) => e.color),
  ])];
  const defs = `<defs>${arrowColors.map(arrow).join('')}</defs>`;

  const inner = defs + body.join('').replace('__W__', String(width)).replace('__H__', String(height));
  return { inner, width, height };
}

const svgDoc = (svg, renderW, renderH) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svg.width} ${svg.height}" width="${Math.round(renderW)}" height="${Math.round(renderH)}">${svg.inner}</svg>`;

// Print CSS rides inside the printed block, so exports.js keeps owning the print
// window and we still get landscape paper.
const PRINT_CSS = `
  @page { size: landscape; margin: 10mm; }
  body { margin: 0 !important; font-family: ${FONT}; color: #16202c; }
  .df-title { display: flex; align-items: center; gap: 10px; border-bottom: 2px solid ${NAVY}; padding-bottom: 8px; margin-bottom: 10px; }
  .df-title h1 { font-size: 17px; margin: 0; color: ${NAVY}; }
  .df-title .sub { font-size: 11.5px; color: ${BLUE}; margin-top: 2px; }
  .df-meta { display: flex; flex-wrap: wrap; gap: 4px 22px; font-size: 10.5px; color: ${GREY}; margin-bottom: 10px; }
  .df-meta b { color: ${NAVY}; }
  .df-note { font-size: 10.5px; color: #8a5a00; background: #fff8e6; border: 1px solid #f0d9a8; padding: 6px 8px; border-radius: 4px; margin-bottom: 10px; }
  .df-legend { border: 1px solid #d7dee6; border-radius: 4px; padding: 8px 10px; margin: 10px 0; font-size: 10.5px; page-break-inside: avoid; }
  .df-legend h2 { font-size: 12px; margin: 0 0 6px; color: ${NAVY}; }
  .df-legend h3 { font-size: 10px; margin: 8px 0 4px; color: ${GREY}; letter-spacing: .4px; }
  .df-legend .items { display: flex; flex-wrap: wrap; gap: 4px 16px; }
  .df-legend .it { display: flex; align-items: center; gap: 6px; width: 240px; }
  .df-legend .sw { display: inline-block; width: 20px; height: 13px; border-radius: 3px; }
  .df-legend .rule { display: inline-block; width: 34px; vertical-align: middle; }
  .df-page { page-break-after: always; }
  .df-page:last-child { page-break-after: auto; }
  .df-mappage { page-break-before: always; }
  .df-tile { overflow: hidden; position: relative; }
  .df-tile > div { position: absolute; }
  .df-cap { font-size: 10px; color: ${GREY}; margin-top: 4px; }
  table.df-tbl { border-collapse: collapse; width: 100%; font-size: 9.5px; }
  table.df-tbl th { text-align: left; color: ${NAVY}; border-bottom: 1px solid #c8d2dd; padding: 3px 5px; }
  table.df-tbl td { border-bottom: 1px solid #eef1f5; padding: 3px 5px; vertical-align: top; }
`;

// ---------------------------------------------------------------------------
export default function DataFlowDiagram({ tenantId, onOpen }) {
  const [sheets, setSheets] = useState(null);
  const [tenantName, setTenantName] = useState(tenantId);

  const [routing, setRouting] = useState(ALL);      // ALL | engine | direct | unknown
  const [netKey, setNetKey] = useState(ALL);
  const [crit, setCrit] = useState(ALL);
  const [linkState, setLinkState] = useState(ALL);  // ALL | linked | unlinked
  const [query, setQuery] = useState('');
  const [zoom, setZoom] = useState(0.55);
  const [pageMode, setPageMode] = useState('fit');  // fit | tile
  const [selectedId, setSelectedId] = useState('');

  // ---- Load -----------------------------------------------------------------
  useEffect(() => {
    let live = true;
    setSheets(null);
    store.listFactSheets(tenantId).then((rows) => { if (live) setSheets(rows || []); });
    return () => { live = false; };
  }, [tenantId]);

  useEffect(() => {
    let live = true;
    store.listTenants().then((list) => {
      if (!live) return;
      const t = (list || []).find((x) => x.id === tenantId);
      setTenantName(t ? t.name : tenantId);
    });
    return () => { live = false; };
  }, [tenantId]);

  useEffect(() => {
    setRouting(ALL);
    setNetKey(ALL);
    setCrit(ALL);
    setLinkState(ALL);
    setQuery('');
    setSelectedId('');
  }, [tenantId]);

  // ---- Model: one record per Interface fact sheet ---------------------------
  const model = useMemo(() => {
    const rows = sheets || [];
    const ifaceSheets = rows.filter((fs) => fs && fs.type === 'Interface');
    const appById = new Map(rows.filter((fs) => fs && fs.type === 'Application').map((fs) => [fs.id, fs]));

    // The centre of the picture. The runbook these interfaces came from is
    // written against Epic, so look for an Epic application fact sheet; if the
    // workspace does not hold one, say that rather than invent a fact sheet.
    const epicCandidates = [...appById.values()].filter((fs) => /\bepic\b/i.test(fs.name || ''));
    epicCandidates.sort((a, b) => a.name.length - b.name.length || a.name.localeCompare(b.name));
    const coreSheet = epicCandidates[0] || null;
    const coreName = coreSheet ? coreSheet.name : 'Epic';

    const items = ifaceSheets.map((fs) => {
      const d = dataOf(fs);
      const name = clean(d.systemVendor) || clean(fs.name) || '(unnamed interface)';

      // Routing is a three-state fact, not a boolean with a default: recorded as
      // through the engine, recorded as not, or simply never recorded.
      let route = 'unknown';
      if (d.viaEngine === true || norm(d.viaEngine) === 'true' || norm(d.viaEngine) === 'yes') route = 'engine';
      else if (d.viaEngine === false || norm(d.viaEngine) === 'false' || norm(d.viaEngine) === 'no') route = 'direct';
      const engineName = clean(d.interfaceEngine) || (route === 'engine' ? 'Interface engine (name not recorded)' : '');

      const rawNet = clean(d.networkFlow);
      const netK = rawNet ? norm(rawNet) : NET_UNRECORDED.key;
      const known = NET_BY_KEY.get(netK);
      const net = known || (rawNet
        ? { key: netK, label: rawNet, color: '#78909C', fill: '#eceff1', unclassified: true, note: 'value not one of the four recorded network paths' }
        : NET_UNRECORDED);

      const mechs = MECHANISM_FIELDS
        .map((m) => ({ label: m.label, ids: asIdList(d[m.field]) }))
        .filter((m) => m.ids.length);
      const fhirRaw = clean(d.fhirOverride);
      const fhir = fhirRaw && !FHIR_NEGATIVE.has(norm(fhirRaw));
      if (fhir) mechs.push({ label: 'FHIR', ids: [], raw: fhirRaw });
      const mechLabel = mechs.length ? mechs.map((m) => m.label).join(' · ') : MECH_NOT_RECORDED;
      const recordNumbers = mechs.filter((m) => m.ids.length).map((m) => `${m.label}: ${m.ids.join(', ')}`).join(' | ');

      const appId = clean(d.applicationId);
      const linkedSheet = appId ? appById.get(appId) : null;
      // A missing confidence is not zero confidence — it is "no score recorded".
      const rawConfidence = clean(d.linkConfidence) === '' ? null : Number(d.linkConfidence);
      const confidence = rawConfidence !== null && Number.isFinite(rawConfidence) ? rawConfidence : null;

      const criticality = clean(d.criticality);
      const owner = clean(d.taskOwner) || clean(d.manager) || clean(d.team);

      return {
        id: fs.id,
        sheetName: clean(fs.name),
        name,
        route,
        engineName,
        engineKey: route === 'engine' ? norm(engineName) : '',
        net,
        rawNet,
        mechs,
        mechLabel,
        mechRecorded: mechs.length > 0,
        recordNumbers,
        fhirRaw,
        criticality,
        team: clean(d.team),
        manager: clean(d.manager),
        taskOwner: clean(d.taskOwner),
        owner,
        notes: clean(d.notes),
        linked: !!appId,
        applicationId: appId,
        applicationName: clean(d.applicationName) || (linkedSheet ? linkedSheet.name : ''),
        applicationExists: !!linkedSheet,
        linkConfidence: confidence,
        linkMethod: clean(d.linkMethod),
      };
    }).sort((a, b) => a.name.localeCompare(b.name));

    const critValues = [...new Set(items.map((i) => i.criticality).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const netValues = [];
    const netSeen = new Set();
    items.forEach((i) => {
      if (netSeen.has(i.net.key)) return;
      netSeen.add(i.net.key);
      netValues.push(i.net);
    });
    netValues.sort((a, b) => {
      const ai = NET_KNOWN.findIndex((n) => n.key === a.key);
      const bi = NET_KNOWN.findIndex((n) => n.key === b.key);
      return (ai === -1 ? 98 : ai) - (bi === -1 ? 98 : bi) || a.label.localeCompare(b.label);
    });

    return { items, coreSheet, coreName, critValues, netValues, appCount: appById.size };
  }, [sheets]);

  // ---- Filters ---------------------------------------------------------------
  const drawn = useMemo(() => {
    const q = norm(query);
    return model.items.filter((i) => {
      if (routing !== ALL && i.route !== routing) return false;
      if (netKey !== ALL && i.net.key !== netKey) return false;
      if (crit !== ALL && (crit === '__none__' ? i.criticality !== '' : i.criticality !== crit)) return false;
      if (linkState === 'linked' && !i.linked) return false;
      if (linkState === 'unlinked' && i.linked) return false;
      if (q && ![i.name, i.owner, i.team, i.manager, i.mechLabel, i.rawNet, i.applicationName, i.recordNumbers]
        .some((v) => norm(v).includes(q))) return false;
      return true;
    });
  }, [model.items, routing, netKey, crit, linkState, query]);

  const counts = useMemo(() => {
    const tally = (list, pick) => {
      const m = new Map();
      list.forEach((i) => m.set(pick(i), (m.get(pick(i)) || 0) + 1));
      return m;
    };
    const byRouteAll = tally(model.items, (i) => i.route);
    const byRouteDrawn = tally(drawn, (i) => i.route);
    const byNetAll = tally(model.items, (i) => i.net.key);
    const byNetDrawn = tally(drawn, (i) => i.net.key);
    return {
      total: model.items.length,
      drawn: drawn.length,
      engineAll: byRouteAll.get('engine') || 0,
      directAll: byRouteAll.get('direct') || 0,
      unknownAll: byRouteAll.get('unknown') || 0,
      engineDrawn: byRouteDrawn.get('engine') || 0,
      directDrawn: byRouteDrawn.get('direct') || 0,
      unknownDrawn: byRouteDrawn.get('unknown') || 0,
      unlinkedAll: model.items.filter((i) => !i.linked).length,
      unlinkedDrawn: drawn.filter((i) => !i.linked).length,
      linkedDrawn: drawn.filter((i) => i.linked).length,
      noMechAll: model.items.filter((i) => !i.mechRecorded).length,
      noMechDrawn: drawn.filter((i) => !i.mechRecorded).length,
      byNetAll,
      byNetDrawn,
    };
  }, [model.items, drawn]);

  // ---- Positioned graph ------------------------------------------------------
  const graph = useMemo(() => {
    if (!drawn.length) return null;

    const engineItems = drawn.filter((i) => i.route === 'engine');
    const sideItems = drawn.filter((i) => i.route !== 'engine');

    // One node per distinct engine actually present in the drawn set.
    const engineMap = new Map();
    engineItems.forEach((i) => {
      const key = i.engineKey || '__engine__';
      if (!engineMap.has(key)) engineMap.set(key, { key, label: i.engineName || 'Interface engine', items: [] });
      engineMap.get(key).items.push(i);
    });
    const engines = [...engineMap.values()].sort((a, b) => b.items.length - a.items.length || a.label.localeCompare(b.label));

    // Lay each engine's comb out locally, then stack the bands vertically and
    // centre the stack on the core system.
    const engineCx = -(HUB_W / 2 + TRUNK_GAP + ENG_W / 2);
    const bands = engines.map((e) => {
      const comb = combLayout(e.items);
      return { engine: e, comb, height: comb.maxY - comb.minY };
    });
    const stackH = bands.reduce((a, b) => a + b.height, 0) + Math.max(0, bands.length - 1) * BAND_GAP;
    let top = -stackH / 2;
    const nodes = [];
    const edges = [];

    bands.forEach((band, bi) => {
      // The engine node sits at the comb's local origin; `-comb.minY` shifts the
      // band's top edge to `top` in stack coordinates.
      const engY = top - band.comb.minY;
      const engId = `engine:${band.engine.key}:${bi}`;
      const n = band.engine.items.length;
      nodes.push({
        id: engId,
        kind: 'engine',
        name: band.engine.label,
        sub: `interface engine · ${n} interface${n === 1 ? '' : 's'}`,
        cx: engineCx,
        cy: engY,
        w: ENG_W,
        h: ENG_H,
        fill: BLUE,
        stroke: NAVY,
      });

      // The collector bus: one vertical channel per column plus the corridor run
      // into the engine. Drawn once, in navy, and labelled with its count — it is
      // shared infrastructure, not a per-interface claim. Every interface still
      // has its own coloured stub onto it.
      band.comb.columns.forEach((col, ci) => {
        edges.push({
          id: `bus:${engId}:${ci}`,
          raw: true,
          noMarker: true,
          x1: engineCx + col.busX,
          y1: engY + Math.min(col.minY, 0),
          x2: engineCx + col.busX,
          y2: engY + Math.max(col.maxY, 0),
          color: TRUNK_COLOR,
          width: 2,
        });
      });
      if (band.comb.columns.length) {
        const far = band.comb.columns[band.comb.columns.length - 1].busX;
        edges.push({
          id: `bus:${engId}:run`,
          raw: true,
          x1: engineCx + far,
          y1: engY,
          x2: engineCx - ENG_W / 2,
          y2: engY,
          color: TRUNK_COLOR,
          width: 3.2,
          label: `${n} interface${n === 1 ? '' : 's'} collected`,
          labelColor: NAVY,
          labelAbove: true,
        });
      }

      band.comb.placed.forEach((p) => {
        nodes.push({
          id: p.id,
          kind: 'system',
          item: p,
          name: p.name,
          net: p.net,
          criticality: p.criticality,
          linked: p.linked,
          hover: hoverText(p, model.coreName),
          cx: engineCx + p.cx,
          cy: engY + p.cy,
          w: NODE_W,
          h: NODE_H,
        });
        edges.push({
          id: `e:${p.id}`,
          raw: true,
          x1: engineCx + p.cx + NODE_W / 2,
          y1: engY + p.cy,
          x2: engineCx + p.busX,
          y2: engY + p.cy,
          color: p.net.color,
          width: ENGINE_WIDTH,
          label: p.mechRecorded ? p.mechLabel : 'not recorded',
          labelColor: p.mechRecorded ? GREY : '#8a5a00',
          labelAbove: true,
        });
      });

      edges.push({
        id: `trunk:${engId}`,
        source: engId,
        target: 'core',
        color: TRUNK_COLOR,
        width: 4,
        label: `${n} interface${n === 1 ? '' : 's'}`,
        labelColor: NAVY,
      });
      top += band.height + BAND_GAP;
    });

    // Direct + not-recorded systems: one column to the right of the core, each on
    // its own line. Direct first, so the true point-to-point integrations sit at
    // the top of the column.
    const rightOrder = [
      ...sideItems.filter((i) => i.route === 'direct'),
      ...sideItems.filter((i) => i.route === 'unknown'),
    ];
    columnLayout(rightOrder, HUB_W / 2 + CORE_COL_GAP + NODE_W / 2).forEach((p) => {
      nodes.push({
        id: p.id,
        kind: 'system',
        item: p,
        name: p.name,
        net: p.net,
        criticality: p.criticality,
        linked: p.linked,
        hover: hoverText(p, model.coreName),
        cx: p.cx,
        cy: p.cy,
        w: NODE_W,
        h: NODE_H,
      });
      edges.push({
        id: `e:${p.id}`,
        source: p.id,
        target: 'core',
        color: p.route === 'direct' ? p.net.color : GREY,
        width: p.route === 'direct' ? DIRECT_WIDTH : 1.6,
        dashed: p.route === 'unknown',
        label: p.route === 'unknown' ? `routing not recorded · ${p.mechLabel}` : p.mechLabel,
        labelColor: p.route === 'direct' ? DIRECT_ACCENT : '#8a5a00',
      });
    });

    nodes.push({
      id: 'core',
      kind: 'core',
      name: model.coreName,
      sub: model.coreSheet ? 'core system' : 'core system — no matching application fact sheet',
      cx: 0,
      cy: 0,
      w: HUB_W,
      h: HUB_H,
      fill: NAVY,
      stroke: NAVY,
    });

    return { nodes, edges, engines, labels: drawn.length <= EDGE_LABEL_LIMIT };
  }, [drawn, model.coreName, model.coreSheet]);

  // ---- Legend model (shared by SVG, screen and PDF) --------------------------
  const legendModel = useMemo(() => {
    const engineLabels = graph ? graph.engines.map((e) => e.label) : [];
    return {
      netRows: model.netValues
        .map((n) => ({ ...n, count: counts.byNetDrawn.get(n.key) || 0 }))
        .filter((n) => n.count > 0),
      coreName: model.coreName,
      engineLabel: engineLabels.length ? engineLabels.join(' / ') : 'the interface engine',
      engineCount: counts.engineDrawn,
      directCount: counts.directDrawn,
      unknownCount: counts.unknownDrawn,
      linkedCount: counts.linkedDrawn,
      unlinkedCount: counts.unlinkedDrawn,
      drawn: counts.drawn,
      total: counts.total,
      labelsHidden: !!graph && !graph.labels,
    };
  }, [graph, model.netValues, model.coreName, counts]);

  const scopeText = useMemo(() => {
    const bits = [];
    bits.push(routing === ALL ? 'all routing' : routing === 'engine' ? 'through the interface engine only' : routing === 'direct' ? 'direct point-to-point only' : 'routing not recorded only');
    bits.push(netKey === ALL ? 'all network paths' : (model.netValues.find((n) => n.key === netKey) || {}).label || netKey);
    bits.push(crit === ALL ? 'all criticalities' : crit === '__none__' ? 'criticality not recorded' : `criticality ${crit}`);
    bits.push(linkState === ALL ? 'linked and unlinked' : linkState === 'linked' ? 'linked to an application only' : 'unlinked only');
    if (query.trim()) bits.push(`search "${query.trim()}"`);
    return bits.join(' · ');
  }, [routing, netKey, crit, linkState, query, model.netValues]);

  const mapSvg = useMemo(
    () => (graph ? buildSvg(graph, { labels: graph.labels, selectedId }) : null),
    [graph, selectedId]
  );

  const fit = useMemo(() => {
    if (!mapSvg) return null;
    const s = Math.min(1, PAGE_W / mapSvg.width, MAP_H / mapSvg.height);
    return {
      scale: s,
      pct: Math.max(1, Math.round(s * 100)),
      cols: Math.ceil(mapSvg.width / PAGE_W),
      rows: Math.ceil(mapSvg.height / MAP_H),
    };
  }, [mapSvg]);

  const subLine = `${counts.drawn} of ${counts.total} interfaces drawn · ${counts.engineDrawn} via an engine · ${counts.directDrawn} direct point-to-point · ${counts.unknownDrawn} routing not recorded · ${counts.unlinkedDrawn} with no application linked · generated ${nowStamp()}`;

  // ---- Exports ---------------------------------------------------------------
  const csvRows = useMemo(() => drawn.map((i) => ({
    source: i.name,
    target: model.coreName,
    viaEngine: i.route === 'engine' ? `Yes — ${i.engineName}` : i.route === 'direct' ? 'No — direct point-to-point' : 'Not recorded',
    mechanism: i.mechLabel,
    records: i.recordNumbers,
    fhir: i.fhirRaw,
    network: i.rawNet || 'not recorded',
    criticality: i.criticality || 'not recorded',
    owner: i.owner || 'not recorded',
    team: i.team,
    manager: i.manager,
    application: i.linked ? i.applicationName : 'NOT LINKED',
    linkConfidence: i.linked && i.linkConfidence !== null ? i.linkConfidence.toFixed(2) : '',
    linkMethod: i.linked ? `inferred — ${i.linkMethod || 'method not recorded'}` : '',
  })), [drawn, model.coreName]);

  const doCsv = useCallback(() => {
    if (!csvRows.length) return;
    exportCsv(`data-flow-edges-${tenantId}-${fileStamp()}.csv`, csvRows, [
      { label: 'Source (third-party system)', value: 'source' },
      { label: 'Target (core system)', value: 'target' },
      { label: 'Via Interface Engine', value: 'viaEngine' },
      { label: 'Epic Interface Mechanism', value: 'mechanism' },
      { label: 'Epic Record Numbers', value: 'records' },
      { label: 'FHIR Override (verbatim)', value: 'fhir' },
      { label: 'Network Path', value: 'network' },
      { label: 'Criticality (as recorded)', value: 'criticality' },
      { label: 'Owner', value: 'owner' },
      { label: 'Team', value: 'team' },
      { label: 'Manager', value: 'manager' },
      { label: 'Linked Application', value: 'application' },
      { label: 'Link Confidence (inferred)', value: 'linkConfidence' },
      { label: 'Link Method', value: 'linkMethod' },
    ]);
  }, [csvRows, tenantId]);

  const doPng = useCallback(() => {
    if (!graph) return;
    const svg = buildSvg(graph, {
      labels: graph.labels,
      header: true,
      legend: true,
      legendModel,
      meta: {
        title: `Primordia — Data Flow · ${tenantName}`,
        scope: scopeText,
        sub: subLine,
      },
    });
    const markup = svgDoc(svg, svg.width, svg.height);
    const blob = new Blob([markup], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const px = Math.min(2, 16000 / Math.max(svg.width, svg.height, 1));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(svg.width * px);
      canvas.height = Math.round(svg.height * px);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      exportCanvasPng(canvas, `data-flow-${tenantId}-${fileStamp()}.png`);
      URL.revokeObjectURL(url);
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  }, [graph, legendModel, tenantId, tenantName, scopeText, subLine]);

  const doPdf = useCallback(() => {
    if (!graph) return;
    printElementToPdf(PRINT_ID, `Primordia — Data Flow — ${tenantName}`);
  }, [graph, tenantName]);

  // ---- Canvas interaction ----------------------------------------------------
  const onCanvasClick = useCallback((e) => {
    const g = e.target && e.target.closest ? e.target.closest('[data-iface]') : null;
    if (!g) return;
    setSelectedId(g.getAttribute('data-iface') || '');
  }, []);

  const selected = useMemo(
    () => model.items.find((i) => i.id === selectedId) || null,
    [model.items, selectedId]
  );

  // ---- Loading ---------------------------------------------------------------
  if (!sheets) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={22} />
        <Typography sx={{ color: GREY }}>Loading the interface catalogue…</Typography>
      </Box>
    );
  }

  // ---- Honest empty state ----------------------------------------------------
  if (!model.items.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD, mb: 2 }}>Data Flow</Typography>
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <HubIcon sx={{ fontSize: 44, color: '#c7d3e0' }} />
          <Typography sx={{ fontWeight: 700, color: HEAD, mt: 1, mb: 0.5 }}>
            No Interface fact sheets in this workspace
          </Typography>
          <Typography variant="body2" sx={{ color: GREY, maxWidth: 680, mx: 'auto' }}>
            This diagram is drawn strictly from <b>Interface</b> fact sheets — one per third-party system that
            exchanges data with the core system. <b>{tenantName}</b> holds{' '}
            {(sheets || []).length.toLocaleString()} fact sheets, none of type Interface, so there is nothing to
            draw and an empty canvas would be misleading.
          </Typography>
          <Typography variant="body2" sx={{ color: GREY, maxWidth: 680, mx: 'auto', mt: 1.5 }}>
            Load them with the <b>interface import</b> — the Epic 3rd-Party Testing Runbook loader is the richest
            source we have: it captures the system, whether it routes through the interface engine, the network
            path, and the Epic interface mechanism (E0E / E0A / FDI / AIP / FHIR). Interfaces created by hand in
            the Inventory work here too, once they carry a system vendor and a network path.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const zoomLabel = `${Math.round(zoom * 100)}%`;

  return (
    <Box sx={{ p: 2 }}>
      {/* ---------------- Header ---------------- */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Data Flow</Typography>
        <Chip
          size="small"
          label={`${counts.drawn} of ${counts.total} interfaces drawn`}
          sx={{ bgcolor: '#e8f0fe', color: NAVY, fontWeight: 700 }}
        />
        <Chip
          size="small"
          label={`${counts.directDrawn} direct (no engine)`}
          sx={{ bgcolor: '#fdeeee', color: '#9d2020', fontWeight: 700, border: '1px solid #f0bcbc' }}
        />
        <Chip
          size="small"
          label={`${counts.unlinkedDrawn} unlinked`}
          sx={{ bgcolor: '#fff3e0', color: '#8a5a00', fontWeight: 700, border: '1px solid #f0c98a' }}
        />
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" startIcon={<PictureAsPdfIcon />} disabled={!graph}
          onClick={doPdf} sx={{ bgcolor: NAVY, '&:hover': { bgcolor: BLUE } }}>
          PDF
        </Button>
        <Button size="small" variant="outlined" startIcon={<ImageIcon />} disabled={!graph} onClick={doPng}>PNG</Button>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={!csvRows.length} onClick={doCsv}>
          CSV edge list
        </Button>
      </Stack>

      <Typography variant="body2" sx={{ color: GREY, mb: 2, maxWidth: 1000 }}>
        Every third-party system that exchanges data with <b>{model.coreName}</b>, and how it gets there. Systems on
        the <b>left</b> route through the interface engine — one chokepoint, one cutover conversation. Systems on the{' '}
        <b>right</b> are <b>direct point-to-point</b> integrations with nothing in between: no central place to
        re-point and no throttle, which is what makes them the migration risk. Node colour is the network path,
        because that is what decides the firewall, egress and latency work.
      </Typography>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
        {/* ---------------- Filters + legend + detail ---------------- */}
        <Paper sx={{ p: 1.5, width: { xs: '100%', lg: 320 }, flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, mb: 1 }}>Filters</Typography>

          <TextField
            select fullWidth size="small" label="Routing" value={routing}
            onChange={(e) => setRouting(e.target.value)} sx={{ mb: 1.25 }}
          >
            <MenuItem value={ALL}>{`All routing (${counts.total})`}</MenuItem>
            <MenuItem value="engine" disabled={!counts.engineAll}>{`Through an interface engine (${counts.engineAll})`}</MenuItem>
            <MenuItem value="direct" disabled={!counts.directAll}>{`Direct point-to-point (${counts.directAll})`}</MenuItem>
            <MenuItem value="unknown" disabled={!counts.unknownAll}>{`Routing not recorded (${counts.unknownAll})`}</MenuItem>
          </TextField>

          <TextField
            select fullWidth size="small" label="Network path" value={netKey}
            onChange={(e) => setNetKey(e.target.value)} sx={{ mb: 1.25 }}
          >
            <MenuItem value={ALL}>{`All network paths (${counts.total})`}</MenuItem>
            {model.netValues.map((n) => (
              <MenuItem key={n.key} value={n.key}>
                {`${n.label} (${counts.byNetAll.get(n.key) || 0})${n.unclassified ? ' — unclassified' : ''}`}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select fullWidth size="small" label="Criticality (as recorded)" value={crit}
            onChange={(e) => setCrit(e.target.value)} sx={{ mb: 1.25 }}
            helperText="Shown verbatim — the source runbook does not define what 0–3 mean."
          >
            <MenuItem value={ALL}>All criticalities</MenuItem>
            {model.critValues.map((c) => (
              <MenuItem key={c} value={c}>
                {`${c} (${model.items.filter((i) => i.criticality === c).length})`}
              </MenuItem>
            ))}
            <MenuItem value="__none__">
              {`Not recorded (${model.items.filter((i) => !i.criticality).length})`}
            </MenuItem>
          </TextField>

          <TextField
            select fullWidth size="small" label="Application link" value={linkState}
            onChange={(e) => setLinkState(e.target.value)} sx={{ mb: 1.25 }}
          >
            <MenuItem value={ALL}>{`Linked and unlinked (${counts.total})`}</MenuItem>
            <MenuItem value="linked">{`Linked to an application (${counts.total - counts.unlinkedAll})`}</MenuItem>
            <MenuItem value="unlinked">{`No application linked (${counts.unlinkedAll})`}</MenuItem>
          </TextField>

          <TextField
            fullWidth size="small" label="Search" value={query} placeholder="system, owner, mechanism, record #…"
            onChange={(e) => setQuery(e.target.value)}
          />

          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, mb: 0.75 }}>Legend</Typography>
          <Typography sx={{ fontSize: 10, color: GREY, fontWeight: 700, letterSpacing: 0.4, mb: 0.5 }}>
            NETWORK PATH (NODE COLOUR)
          </Typography>
          <Stack spacing={0.4} sx={{ mb: 1 }}>
            {legendModel.netRows.length ? legendModel.netRows.map((n) => (
              <Stack key={n.key} direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{
                  width: 20, height: 13, borderRadius: '3px', bgcolor: n.fill, mt: '2px', flexShrink: 0,
                  border: `1.5px ${n.unclassified ? 'dashed' : 'solid'} ${n.color}`,
                }} />
                <Box>
                  <Typography sx={{ fontSize: 11.5, color: NAVY, fontWeight: 600 }}>
                    {`${n.label} (${n.count})`}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: GREY }}>{n.note}</Typography>
                </Box>
              </Stack>
            )) : (
              <Typography sx={{ fontSize: 11.5, color: GREY }}>Nothing drawn.</Typography>
            )}
          </Stack>

          <Typography sx={{ fontSize: 10, color: GREY, fontWeight: 700, letterSpacing: 0.4, mb: 0.5 }}>
            ROUTING (WHICH SIDE, AND HOW THE LINE RUNS)
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ width: 32, borderTop: `${DIRECT_WIDTH}px solid ${DIRECT_ACCENT}`, mt: '7px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11.5, color: NAVY }}>
                {`Right of ${model.coreName} — direct point-to-point, its own unshared line, no engine (${counts.directDrawn})`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ width: 32, borderTop: `${ENGINE_WIDTH}px solid ${GREY}`, mt: '7px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11.5, color: NAVY }}>
                {`Left — one stub per interface onto the collector bus, then through ${legendModel.engineLabel} (${counts.engineDrawn})`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ width: 32, borderTop: `1.6px dashed ${GREY}`, mt: '7px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11.5, color: NAVY }}>
                {`Dashed — routing not recorded (${counts.unknownDrawn})`}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 10.5, color: GREY }}>
              The navy bus and engine trunk are shared infrastructure, drawn once and labelled with their count.
              Every interface still has its own coloured stub — no line exists without an Interface fact sheet
              behind it.
            </Typography>
          </Stack>

          <Typography sx={{ fontSize: 10, color: GREY, fontWeight: 700, letterSpacing: 0.4, mb: 0.5 }}>
            NODE MARKERS
          </Typography>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: BLUE, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11.5, color: NAVY }}>
                {`Linked to an application — inferred by name match (${counts.linkedDrawn})`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: `1.5px dashed ${GREY}`, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 11.5, color: NAVY }}>
                {`No application fact sheet behind it (${counts.unlinkedDrawn})`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{
                width: 17, height: 12, borderRadius: '2.5px', bgcolor: NAVY, color: '#fff',
                fontSize: 8.5, fontWeight: 700, textAlign: 'center', lineHeight: '12px', flexShrink: 0,
              }}>
                0
              </Box>
              <Typography sx={{ fontSize: 11.5, color: NAVY }}>Criticality, verbatim from the source</Typography>
            </Stack>
          </Stack>

          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, mb: 1 }}>PDF pagination</Typography>
          <TextField
            select fullWidth size="small" value={pageMode} onChange={(e) => setPageMode(e.target.value)}
            helperText={fit
              ? (pageMode === 'fit'
                ? `Scales to ${fit.pct}% on one landscape page — the scale factor is printed on the page.`
                : `Tiles at 100% across ${fit.cols * fit.rows} page(s) (${fit.cols} × ${fit.rows}), each page captioned.`)
              : 'Nothing drawn yet.'}
          >
            <MenuItem value="fit">Scale to fit one landscape page</MenuItem>
            <MenuItem value="tile">Full size, tiled across pages</MenuItem>
          </TextField>

          {selected && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, mb: 0.5 }}>{selected.name}</Typography>
              <DetailRow label="Routing" value={
                selected.route === 'engine' ? `Through ${selected.engineName}`
                  : selected.route === 'direct' ? `Direct point-to-point to ${model.coreName} — no engine`
                    : 'Not recorded in the source'
              } />
              <DetailRow label="Network path" value={selected.rawNet || 'not recorded'} warn={!selected.rawNet || selected.net.unclassified} />
              <DetailRow label="Mechanism" value={selected.mechLabel} warn={!selected.mechRecorded} />
              {selected.mechs.filter((m) => m.ids.length).map((m) => (
                <DetailRow key={m.label} label={`${m.label} records`} value={m.ids.join(', ')} />
              ))}
              {selected.fhirRaw ? <DetailRow label="FHIR override (verbatim)" value={selected.fhirRaw} /> : null}
              <DetailRow label="Criticality" value={selected.criticality || 'not recorded'} warn={!selected.criticality} />
              <DetailRow label="Team" value={selected.team || 'not recorded'} />
              <DetailRow label="Manager" value={selected.manager || 'not recorded'} />
              <DetailRow label="Task owner" value={selected.taskOwner || 'not recorded'} />
              <DetailRow
                label="Application"
                warn={!selected.linked}
                value={selected.linked
                  ? `${selected.applicationName || selected.applicationId} — inferred${selected.linkConfidence !== null ? `, confidence ${selected.linkConfidence.toFixed(2)}` : ''}${selected.linkMethod ? ` (${selected.linkMethod})` : ''}`
                  : 'NOT LINKED — no application fact sheet behind this interface'}
              />
              {selected.linked && !selected.applicationExists && (
                <Typography sx={{ fontSize: 11, color: '#8a5a00' }}>
                  The linked application id is not present in this workspace.
                </Typography>
              )}
              {selected.notes ? <DetailRow label="Notes" value={selected.notes} /> : null}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {onOpen && (
                  <Button size="small" variant="outlined" onClick={() => onOpen(selected.id)}>
                    Open interface
                  </Button>
                )}
                {onOpen && selected.linked && selected.applicationExists && (
                  <Button size="small" variant="outlined" onClick={() => onOpen(selected.applicationId)}>
                    Open application
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Paper>

        {/* ---------------- Canvas ---------------- */}
        <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
          {counts.unknownAll > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>
                {`${counts.unknownAll} interface${counts.unknownAll === 1 ? '' : 's'} do not record whether they route through an engine`}
              </AlertTitle>
              <Typography sx={{ fontSize: 12.5, color: GREY }}>
                They are drawn on the direct side with a dashed line, because no intermediary is recorded — that is
                a statement about the data, not about the architecture. Confirm each one before cutover planning.
              </Typography>
            </Alert>
          )}

          {counts.unlinkedAll > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>
                {`${counts.unlinkedAll} of ${counts.total} interfaces have no application fact sheet behind them`}
              </AlertTitle>
              <Typography sx={{ fontSize: 12.5, color: GREY }}>
                They are drawn with a hollow dot rather than hidden. An integration nobody owns is exactly the kind
                of thing a discovery is supposed to find. Where a link does exist it was <b>inferred</b> by name
                match and carries its confidence — treat it as a candidate, not a confirmation.
              </Typography>
            </Alert>
          )}

          {counts.noMechAll > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>
                {`${counts.noMechAll} interface${counts.noMechAll === 1 ? '' : 's'} record no Epic interface mechanism`}
              </AlertTitle>
              <Typography sx={{ fontSize: 12.5, color: GREY }}>
                Every E0E / E0A / FDI / AIP field was empty and there is no FHIR override, so those edges read
                “{MECH_NOT_RECORDED}”. Nothing has been guessed from the system name.
              </Typography>
            </Alert>
          )}

          {!graph && (
            <Alert severity="warning">
              <AlertTitle sx={{ fontWeight: 700 }}>Nothing matches the current filters</AlertTitle>
              {`This workspace holds ${counts.total} interface${counts.total === 1 ? '' : 's'}, but none of them match: ${scopeText}. Widen a filter to draw the flow again.`}
            </Alert>
          )}

          {graph && mapSvg && (
            <Paper sx={{ p: 0, position: 'relative' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13 }}>
                  {`${model.coreName} data flow`}
                </Typography>
                <Chip
                  size="small"
                  label={`${counts.drawn} of ${counts.total} interfaces drawn`}
                  sx={{ height: 20, fontSize: 11, bgcolor: '#eef3f9', color: NAVY, fontWeight: 700 }}
                />
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<ZoomOutIcon />} onClick={() => setZoom((z) => Math.max(0.25, Math.round((z - 0.15) * 100) / 100))}>
                  Out
                </Button>
                <Typography sx={{ fontSize: 12, color: GREY, minWidth: 44, textAlign: 'center' }}>{zoomLabel}</Typography>
                <Button size="small" startIcon={<ZoomInIcon />} onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.15) * 100) / 100))}>
                  In
                </Button>
              </Stack>
              <Divider />
              <Box
                onClick={onCanvasClick}
                sx={{ overflow: 'auto', maxHeight: 760, bgcolor: '#fff', p: 1 }}
                dangerouslySetInnerHTML={{ __html: svgDoc(mapSvg, mapSvg.width * zoom, mapSvg.height * zoom) }}
              />
              <Divider />
              <Typography sx={{ fontSize: 11.5, color: GREY, p: 1.25 }}>
                Click a system to see its mechanism, record numbers, owner and link status. Hover for the same detail
                as a tooltip. What you see here is exactly what the PDF and PNG contain — one drawing routine feeds
                all three.
                {!graph.labels && ` Edge labels are hidden above ${EDGE_LABEL_LIMIT} drawn interfaces; the mechanism of every interface is in the table below and in the CSV.`}
                {fit && pageMode === 'fit' && ` The PDF prints at ${fit.pct}% of full size on one landscape page.`}
                {fit && pageMode === 'tile' && ` The PDF prints full size across ${fit.cols * fit.rows} landscape page(s).`}
              </Typography>
            </Paper>
          )}
        </Box>
      </Stack>

      {/* ---------------- Hidden print block (the PDF payload) ---------------- */}
      <div id={PRINT_ID} style={{ display: 'none' }}>
        <style>{PRINT_CSS}</style>
        {graph && mapSvg && fit && (
          <>
            <div className="df-title">
              <PrimordiaLogo size={30} />
              <div>
                <h1>Primordia — Data Flow</h1>
                <div className="sub">{tenantName}</div>
              </div>
            </div>
            <div className="df-meta">
              <span><b>Core system:</b> {model.coreName}</span>
              <span><b>Drawn:</b> {counts.drawn} of {counts.total} interfaces</span>
              <span><b>Through an engine:</b> {counts.engineDrawn}</span>
              <span><b>Direct point-to-point:</b> {counts.directDrawn}</span>
              <span><b>Routing not recorded:</b> {counts.unknownDrawn}</span>
              <span><b>No application linked:</b> {counts.unlinkedDrawn}</span>
              <span><b>Filters:</b> {scopeText}</span>
              <span><b>Generated:</b> {nowStamp()}</span>
              <span><b>Workspace:</b> {tenantId}</span>
            </div>
            <div className="df-note">
              {pageMode === 'fit'
                ? `The diagram follows on its own landscape page, scaled to ${fit.pct}% of full size (nothing is cropped). Page layout assumes Letter or A4 landscape with 10 mm margins.`
                : `The diagram follows at 100%, tiled across ${fit.cols * fit.rows} landscape page(s) in a ${fit.cols} × ${fit.rows} grid — read left to right, top to bottom. Nothing is cropped.`}
              {!graph.labels && ` Per-edge mechanism labels are omitted above ${EDGE_LABEL_LIMIT} drawn interfaces; the mechanism and record numbers of every interface are in the table at the end.`}
            </div>

            <div className="df-legend">
              <h2>Legend</h2>
              <h3>NETWORK PATH (NODE COLOUR)</h3>
              <div className="items">
                {legendModel.netRows.map((n) => (
                  <span className="it" key={n.key}>
                    <span
                      className="sw"
                      style={{ background: n.fill, border: `1.5px ${n.unclassified ? 'dashed' : 'solid'} ${n.color}` }}
                    />
                    <span>{n.label} ({n.count}){n.unclassified ? ' — unclassified' : ''}</span>
                  </span>
                ))}
              </div>
              <h3>ROUTING (WHICH SIDE OF THE CORE, AND HOW THE LINE RUNS)</h3>
              <div>
                <span className="rule" style={{ borderTop: `${DIRECT_WIDTH}px solid ${DIRECT_ACCENT}` }} />
                &nbsp;→&nbsp; <b>Right</b> of {model.coreName} — direct point-to-point, its own unshared line, no
                interface engine in the path ({counts.directDrawn})
              </div>
              <div>
                <span className="rule" style={{ borderTop: `${ENGINE_WIDTH}px solid ${GREY}` }} />
                &nbsp;→&nbsp; <b>Left</b> — one stub per interface onto the collector bus, then through{' '}
                {legendModel.engineLabel} ({counts.engineDrawn})
              </div>
              <div>
                <span className="rule" style={{ borderTop: `1.6px dashed ${GREY}` }} />
                &nbsp;→&nbsp; Dashed — whether it routes through an engine is not recorded ({counts.unknownDrawn})
              </div>
              <div style={{ marginTop: 4 }}>
                The navy bus and the engine trunk are shared infrastructure, drawn once and labelled with their
                count. Every interface still has its own coloured stub onto the bus — no line on this page exists
                without an Interface fact sheet behind it.
              </div>
              <h3>NODE MARKERS</h3>
              <div>
                ● filled dot — linked to an application fact sheet ({counts.linkedDrawn}). The link was
                <b> inferred</b> by name match and carries a confidence score per interface; it is a candidate, not a
                confirmation.
              </div>
              <div>
                ○ hollow dot — no application fact sheet behind this interface ({counts.unlinkedDrawn}). Drawn, not
                hidden: an integration nobody owns is a discovery finding.
              </div>
              <div>
                Numbered badge — criticality exactly as recorded in the source runbook (0–3). The source does not
                define the scale, so it is not re-interpreted here.
              </div>
              <div style={{ marginTop: 4 }}>
                Edge labels give the Epic interface mechanism (E0E / E0A / FDI / AIP / FHIR).
                “{MECH_NOT_RECORDED}” means every mechanism field was empty — nothing has been inferred.
              </div>
              <div style={{ marginTop: 4, fontWeight: 700, color: NAVY }}>
                {counts.drawn} of {counts.total} interfaces drawn.
              </div>
            </div>

            {pageMode === 'fit' ? (
              <div className="df-page df-mappage">
                <div
                  dangerouslySetInnerHTML={{
                    __html: svgDoc(mapSvg, mapSvg.width * fit.scale, mapSvg.height * fit.scale),
                  }}
                />
                <div className="df-cap">
                  {`${tenantName} — ${model.coreName} data flow — ${counts.drawn} of ${counts.total} interfaces — whole diagram on one page at ${fit.pct}% of full size, nothing cropped.`}
                </div>
              </div>
            ) : (
              Array.from({ length: fit.cols * fit.rows }).map((_, i) => {
                const c = i % fit.cols;
                const r = Math.floor(i / fit.cols);
                return (
                  <div className="df-page df-mappage" key={`tile-${r}-${c}`}>
                    <div
                      className="df-tile"
                      style={{ width: PAGE_W, height: MAP_H }}
                      dangerouslySetInnerHTML={{
                        __html: `<div style="left:${-c * PAGE_W}px;top:${-r * MAP_H}px">${svgDoc(mapSvg, mapSvg.width, mapSvg.height)}</div>`,
                      }}
                    />
                    <div className="df-cap">
                      {`Diagram tile row ${r + 1} of ${fit.rows}, column ${c + 1} of ${fit.cols} — ${tenantName} — ${model.coreName} data flow — 100% scale`}
                    </div>
                  </div>
                );
              })
            )}

            <h2 style={{ fontSize: 13, color: NAVY, margin: '12px 0 6px' }}>
              {`Interface list — all ${csvRows.length} drawn`}
            </h2>
            <table className="df-tbl">
              <thead>
                <tr>
                  <th>System / vendor</th><th>Routing</th><th>Mechanism</th><th>Epic records</th>
                  <th>Network path</th><th>Crit.</th><th>Owner</th><th>Application link</th>
                </tr>
              </thead>
              <tbody>
                {csvRows.map((r, i) => (
                  <tr key={`p-${drawn[i].id}`}>
                    <td>{r.source}</td>
                    <td>{r.viaEngine}</td>
                    <td>{r.mechanism}</td>
                    <td>{r.records || '—'}</td>
                    <td>{r.network}</td>
                    <td>{r.criticality}</td>
                    <td>{r.owner}</td>
                    <td>
                      {r.application}
                      {r.linkConfidence ? ` (inferred, ${r.linkConfidence})` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* ---------------- On-screen interface table ---------------- */}
      {drawn.length > 0 && (
        <Paper sx={{ p: 1.5, mt: 2, overflowX: 'auto' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14 }}>Interfaces drawn</Typography>
            <Chip
              size="small"
              label={`${counts.drawn} of ${counts.total}`}
              sx={{ height: 20, fontSize: 11, bgcolor: '#eef3f9', color: NAVY, fontWeight: 700 }}
            />
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 11.5, color: GREY }}>
              Every row is one Interface fact sheet and one edge on the diagram — nothing summarised away.
            </Typography>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: HEAD, fontSize: 12, whiteSpace: 'nowrap' } }}>
                <TableCell>System / vendor</TableCell>
                <TableCell>Routing</TableCell>
                <TableCell>Mechanism</TableCell>
                <TableCell>Epic records</TableCell>
                <TableCell>Network path</TableCell>
                <TableCell>Crit.</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Application link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drawn.map((i) => (
                <TableRow
                  key={i.id}
                  hover
                  selected={i.id === selectedId}
                  onClick={() => setSelectedId(i.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ fontSize: 12, fontWeight: 600, color: NAVY }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{
                        width: 16, height: 12, borderRadius: '2px', bgcolor: typeMeta('Interface').color,
                        color: '#fff', fontSize: 8, fontWeight: 700, textAlign: 'center', lineHeight: '12px',
                      }}>
                        I
                      </Box>
                      <span>{i.name}</span>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {i.route === 'engine' ? (
                      <Tooltip title={i.engineName}><span style={{ color: GREY }}>via engine</span></Tooltip>
                    ) : i.route === 'direct' ? (
                      <b style={{ color: '#9d2020' }}>direct</b>
                    ) : (
                      <span style={{ color: '#8a5a00' }}>not recorded</span>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: i.mechRecorded ? BLUE : '#8a5a00' }}>{i.mechLabel}</TableCell>
                  <TableCell sx={{ fontSize: 11.5, color: GREY, maxWidth: 220 }}>{i.recordNumbers || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    <Chip
                      size="small"
                      label={i.rawNet || 'not recorded'}
                      sx={{
                        height: 20, fontSize: 11, fontWeight: 600, bgcolor: i.net.fill, color: NAVY,
                        border: `1.5px ${i.net.unclassified ? 'dashed' : 'solid'} ${i.net.color}`,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: i.criticality ? NAVY : GREY }}>
                    {i.criticality || '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: GREY }}>{i.owner || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {i.linked ? (
                      <Tooltip title={`Inferred link${i.linkMethod ? ` — ${i.linkMethod}` : ''}${i.linkConfidence !== null ? `, confidence ${i.linkConfidence.toFixed(2)}` : ''}`}>
                        <span style={{ color: BLUE }}>
                          {i.applicationName || i.applicationId}
                          {i.linkConfidence !== null ? ` (inferred ${i.linkConfidence.toFixed(2)})` : ' (inferred)'}
                        </span>
                      </Tooltip>
                    ) : (
                      <span style={{ color: '#8a5a00', fontWeight: 600 }}>not linked</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Hover text: the whole record for one interface, as an SVG <title>. Anything
// missing says so rather than being left blank and read as "none".
// ---------------------------------------------------------------------------
function hoverText(i, coreName) {
  const lines = [
    i.name,
    i.route === 'engine' ? `Routing: through ${i.engineName}` : i.route === 'direct' ? `Routing: DIRECT point-to-point to ${coreName} — no interface engine` : 'Routing: not recorded',
    `Network path: ${i.rawNet || 'not recorded'}`,
    `Mechanism: ${i.mechLabel}`,
  ];
  if (i.recordNumbers) lines.push(`Epic records: ${i.recordNumbers}`);
  if (i.fhirRaw) lines.push(`FHIR override: ${i.fhirRaw}`);
  lines.push(`Criticality (verbatim): ${i.criticality || 'not recorded'}`);
  lines.push(`Owner: ${i.owner || 'not recorded'}`);
  lines.push(i.linked
    ? `Application: ${i.applicationName || i.applicationId} — inferred${i.linkConfidence !== null && i.linkConfidence !== undefined ? `, confidence ${Number(i.linkConfidence).toFixed(2)}` : ''}${i.linkMethod ? ` (${i.linkMethod})` : ''}`
    : 'Application: NOT LINKED — no application fact sheet behind this interface');
  return lines.join('\n');
}

function DetailRow({ label, value, warn }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.25 }} alignItems="flex-start">
      <Typography sx={{ fontSize: 11, color: GREY, width: 104, flexShrink: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: 11.5, color: warn ? '#8a5a00' : NAVY, fontWeight: warn ? 700 : 500, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Stack>
  );
}
